export default class PegasusLazyLoader {
	constructor() {
		this.loadingElements = new Set();
		this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
		this.defaultOptions = {
			root: null,
			rootMargin: '200px',
			threshold: 0,
		};
		this.init = this.init.bind(this);
		this.observeNewImages = this.observeNewImages.bind(this);
		this.observeNewVideos = this.observeNewVideos.bind(this);
		if ('requestIdleCallback' in window) {
			requestIdleCallback(this.init);
		} else {
			setTimeout(this.init, 1);
		}
	}

	init() {
		this.observeNewImages();
		this.observeNewVideos();
		this.setupConnectionWatcher();
	}

	calculatePriority(el) {
		const rect = el.getBoundingClientRect();
		const viewportDistance = rect.top - window.innerHeight;
		const isVisible = viewportDistance < window.innerHeight;
		return isVisible ? 1 : 1 / Math.max(1, viewportDistance);
	}

	// Observe new images (for dynamic content)
	observeNewImages() {
		const images = document.querySelectorAll('img[data-src]:not([data-pegasus-observed])');
		const sorted = Array.from(images)
			.map((img) => {
				img.setAttribute('data-pegasus-observed', '1');
				return {
					element: img,
					priority: this.calculatePriority(img),
				};
			})
			.sort((a, b) => b.priority - a.priority);
		if (!this.observer) {
			this.setupObserver();
		}
		sorted.forEach(({ element }) => this.observer.observe(element));
	}

	// Observe new videos (for dynamic content)
	observeNewVideos() {
		const videos = document.querySelectorAll('video.pegasus-lazy-video:not([data-pegasus-observed])');
		videos.forEach((video) => {
			video.setAttribute('data-pegasus-observed', '1');
			if (!this.observer) {
				this.setupObserver();
			}
			this.observer.observe(video);
		});
	}

	// IntersectionObserver callback — processes entries immediately to avoid
	// losing intersections when the user scrolls quickly.
	setupObserver() {
		if (this.observer) {
			this.observer.disconnect();
		}
		const processEntries = (entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !this.loadingElements.has(entry.target)) {
					this.queueElement(entry.target);
				}
			});
		};
		this.observer = new IntersectionObserver(processEntries, this.defaultOptions);
		// Observe all current images and videos
		const images = document.querySelectorAll('img[data-src][data-pegasus-observed]');
		images.forEach((img) => this.observer.observe(img));
		const videos = document.querySelectorAll('video.pegasus-lazy-video[data-pegasus-observed]');
		videos.forEach((video) => this.observer.observe(video));
	}

	setupConnectionWatcher() {
		if (this.connection) {
			this.connection.addEventListener('change', () => {
				this.updateLoadingStrategy();
			});
		}
	}

	updateLoadingStrategy() {
		if (!this.connection) return;
		if (this.connection.saveData) {
			this.defaultOptions.rootMargin = '0px';
		} else if (this.connection.effectiveType === '4g') {
			this.defaultOptions.rootMargin = '400px';
		} else if (this.connection.effectiveType === '3g') {
			this.defaultOptions.rootMargin = '200px';
		} else {
			this.defaultOptions.rootMargin = '100px';
		}
		if (this.observer) {
			this.disconnect();
			this.setupObserver();
		}
	}

	queueElement(el) {
		if (this.loadingElements.has(el)) return;
		this.loadingElements.add(el);
		if (el.tagName === 'IMG') {
			this.loadImage(el).finally(() => {
				this.loadingElements.delete(el);
				if (this.observer) this.observer.unobserve(el);
			});
		} else if (el.tagName === 'VIDEO') {
			this.loadVideo(el).finally(() => {
				this.loadingElements.delete(el);
				if (this.observer) this.observer.unobserve(el);
			});
		}
	}

	async loadImage(img) {
		const wrapper = img.closest('.pegasus-lazy-wrapper');
		const picture = img.closest('picture');
		try {
			// Create a promise that resolves when the image finishes loading
			const loaded = new Promise((resolve, reject) => {
				// If the image is already complete (cached), resolve immediately
				if (img.complete && img.naturalWidth > 0) {
					resolve();
					return;
				}
				img.addEventListener('load', resolve, { once: true });
				img.addEventListener('error', reject, { once: true });
			});

			if (picture) {
				const sources = picture.querySelectorAll('source[data-srcset]');
				sources.forEach((source) => {
					if (source.dataset.srcset) {
						source.srcset = source.dataset.srcset;
						source.sizes = source.dataset.sizes;
						delete source.dataset.srcset;
						delete source.dataset.sizes;
					}
				});
			}
			if (img.dataset.srcset) {
				img.srcset = img.dataset.srcset;
				img.sizes = img.dataset.sizes;
				delete img.dataset.srcset;
				delete img.dataset.sizes;
			}
			if (img.dataset.src) {
				img.src = img.dataset.src;
				delete img.dataset.src;
			}

			// Wait for the image to actually finish downloading before fading in
			await loaded;

			if (wrapper) {
				wrapper.classList.add('is-loaded');
			}
			if (this.observer) this.observer.unobserve(img);
		} catch (error) {
			console.warn('Image loading failed:', error);
			if (img.dataset.srcset) {
				img.srcset = img.dataset.srcset;
				img.sizes = img.dataset.sizes;
			}
			if (img.dataset.src) {
				img.src = img.dataset.src;
			}
			if (wrapper) {
				wrapper.classList.add('is-loaded');
			}
			if (!img.src) {
				img.src = '/wp-content/themes/pegasus/assets/images/fallback.png';
			}
		}
	}

	async loadVideo(video) {
		try {
			const sources = video.querySelectorAll('source[data-src]');
			sources.forEach((source) => {
				if (source.dataset.src) {
					source.src = source.dataset.src;
					delete source.dataset.src;
				}
			});
			video.load();
			video.classList.remove('pegasus-lazy-video');
			if (this.observer) this.observer.unobserve(video);
		} catch (error) {
			console.warn('Video loading failed:', error);
		}
	}

	disconnect() {
		if (this.observer) {
			this.observer.disconnect();
		}
		this.loadingElements.clear();
	}
}

// Initialize when the DOM is ready
window.PegasusLazyLoaderInstance = null;
function initPegasusLazyLoader() {
	if (!window.PegasusLazyLoaderInstance) {
		window.PegasusLazyLoaderInstance = new PegasusLazyLoader();
	}
}
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initPegasusLazyLoader);
} else {
	initPegasusLazyLoader();
}
